const wishlistName = 'main_wishlist';

/**
 * Creates a new wishlist with the name specified in `wishlistName`.
 *
 * @returns {Promise<Object|boolean>} A promise that resolves to the wishlist object, or `false` if `window._swat` is not defined.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
const createWishlist = () => {
  if (!window._swat) {
    console.error('window._swat is not defined');
    return false;
  }
  return new Promise((resolve, reject) => {
    window._swat.createList(
      {
        lname: wishlistName,
      },
      function (newList) {
        resolve(newList);
      },
      function (error) {
        reject(new Error('Error while creating a List: ', { cause: error }));
      },
    );
  });
};

/**
 * Retrieves the wishlist with the name specified by `wishlistName`.
 *
 * @returns {Promise<Object|boolean>} A promise that resolves to the wishlist object, or `false` if `window._swat` is not defined.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
const getWishlist = () => {
  if (!window._swat) {
    console.error('window._swat is not defined');
    return false;
  }
  return new Promise((resolve, reject) => {
    window._swat.fetchLists({
      callbackFn: async function (lists) {
        const foundList = lists.find((list) => list.lname == wishlistName);
        if (foundList) resolve(foundList);
        const newlist = await createWishlist();
        resolve(newlist);
      },
      errorFn: function (error) {
        reject(
          new Error('Error while fetching all Lists: ', {
            cause: error,
          }),
        );
      },
    });
  });
};

/**
 * Adds a product to the wishlist.
 *
 * @param {Object} [options] - Options for adding the product to the wishlist.
 * @param {string} [options.variantID] - Unique variant ID of the product.
 * @param {string} [options.masterID] - Master product ID of the product.
 * @param {string} [options.productURL] - Product URL.
 *
 * @returns {Promise<Object|boolean>} A promise that resolves to the added product
 * object, or `false` if the product was not added. If there is an error, the promise
 * is rejected with an Error object.
 */
const addToWishlist = async (
  { variantID = '', masterID = '', productURL = '', compareAtPrice = '', price = '' } = {
    variantID: '',
    masterID: '',
    productURL: '',
    compareAtPrice: '',
    price: '',
  },
) => {
  if (!variantID || !masterID || !productURL) {
    console.error(
      'Trying to add product to wishlist with missing required parameters: variantID, masterID, productURL',
    );
    return false;
  }
  if (!window._swat) {
    console.error('window._swat is not defined');
    return false;
  }
  const wishlist = await getWishlist();

  if (wishlist.lid) {
    return new Promise(function (resolve, reject) {
      window._swat.addToList(
        wishlist.lid, // list id
        {
          epi: variantID, // Unique variant ID of the product
          empi: masterID, // Master Product Id of the product
          du: productURL, // Product URL
          cprops: { compare_at_price: compareAtPrice, price: price }, // Compare at price and price of the product
        },
        function (addedListItem) {
          resolve(addedListItem);
        },
        function (error) {
          reject(
            new Error('Error while adding product to wishlist: ', {
              cause: error,
            }),
          );
        },
      );
    });
  } else {
    console.error('wishlist.lid is not defined while trying to add product to wishlist');
    return false;
  }
};

/**
 * Deletes a product from the wishlist.
 *
 * @param {Object} [options] - Options for deleting the product from the wishlist.
 * @param {string} [options.variantID] - Unique variant ID of the product.
 * @param {string} [options.masterID] - Master product ID of the product.
 * @param {string} [options.productURL] - Product URL.
 *
 * @returns {Promise<Object|boolean>} A promise that resolves to the deleted product object, or `false` if the product was not deleted. If there is an error, the promise is rejected with an Error object.
 * @throws {Error} Will throw an error if the network request fails or if the response is not ok.
 */
const deleteFromWishlist = async (
  { variantID = '', masterID = '', productURL = '' } = {
    variantID: '',
    masterID: '',
    productURL: '',
  },
) => {
  if (!variantID || !masterID || !productURL) {
    console.error(
      'Trying to delete product from wishlist with missing required parameters: variantID, masterID, productURL',
    );
    return false;
  }

  if (!window._swat) {
    console.error('window._swat is not defined');
    return false;
  }

  const wishlist = await getWishlist();
  if (!wishlist) {
    return true;
  }

  if (wishlist.lid) {
    return new Promise(function (resolve, reject) {
      window._swat.deleteFromList(
        wishlist.lid, // list id
        {
          epi: variantID, // Unique variant ID of the product
          empi: masterID, // Master Product Id of the product
          du: productURL, // Product URL
        },
        function (deletedProduct) {
          resolve(deletedProduct);
        },
        function (error) {
          reject(
            new Error('Error while deleting product from wishlist: ', {
              cause: error,
            }),
          );
        },
      );
    });
  } else {
    console.warn('wishlist.lid is not defined while trying to delete product from wishlist');
    return false;
  }
};

/**
 * Mirrors `data-wishlist-status` onto `aria-pressed`, so the toggle state is
 * exposed to assistive technology and not only to CSS.
 *
 * @param {HTMLElement} button
 */
const syncWishlistPressed = (button) => {
  if (!button || button.tagName !== 'BUTTON') return;
  button.setAttribute('aria-pressed', String(button.dataset.wishlistStatus === 'active'));
};

/**
 * Sets `data-wishlist-status` and keeps `aria-pressed` in sync.
 *
 * @param {HTMLElement} button
 * @param {'active'|'inactive'} status
 */
const setWishlistStatus = (button, status) => {
  if (!button) return;
  button.dataset.wishlistStatus = status;
  syncWishlistPressed(button);
};

/**
 * Updates the wishlist buttons by finding the corresponding product in the
 * wishlist and updating the `data-wishlist-status` attribute accordingly.
 *
 * @returns {Promise<void>}
 */
const updateWishlistBtns = async () => {
  const wishlist = await getWishlist();
  document.querySelectorAll('[data-wishlist-btn]').forEach((button) => {
    if (window._swat) {
      button.removeAttribute('data-wishlist-blocking');
    }
    if (button?.dataset?.wishlistStatus != 'active' && button?.dataset?.wishlistStatus != 'inactive') {
      const product = wishlist?.listcontents?.find(
        (product) =>
          product.epi == button?.dataset?.wishlistVariantId && product.empi == button?.dataset?.wishlistProductId,
      );
      setWishlistStatus(button, product ? 'active' : 'inactive');
    } else {
      syncWishlistPressed(button);
    }
  });
};

/**
 * Initializes the wishlist by adding the `updateWishlistBtns` function to the
 * global `window.SwymCallbacks` array. This array is used by the Swym app to
 * execute functions after the wishlist is loaded.
 */
const initFirstWishlit = () => {
  if (!window.SwymCallbacks) window.SwymCallbacks = [];
  window.SwymCallbacks.push(updateWishlistBtns);
};

export {
  addToWishlist,
  deleteFromWishlist,
  getWishlist,
  createWishlist,
  initFirstWishlit,
  updateWishlistBtns,
  wishlistName,
  setWishlistStatus,
};
